import React, { useCallback, useEffect, useState } from 'react';
import { focus } from 'util/dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import allResultsSearchIcon from 'assets/images/all-results-search-icon.svg';
import atlassianIntelligenceIconUrl from 'assets/images/atlassian-intelligence-icon-background.svg';
import enterIconUrl from 'assets/images/enter-icon.svg';
import { isHelpCenterAIAnswersEAPEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { useRouter } from 'react-resource-router';
import { createSelector } from 'react-sweet-state';
import { getAccountId } from 'state/selectors/user';
import { useConversation } from 'unified-help-center-alpha/src/store/converse';
import { getProductIdentifierAttributes } from 'unified-help-center-alpha/src/utils/analytics/get-product-identifier-attributes';
import { SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import * as bp from 'view/styles/breakpoints';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import { sizes } from 'view/styles/sizes-viewport';
import { searchTextbox } from 'view/styles/z-index';
import Button from '@atlaskit/button';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ExperienceType } from '@atlassian/conversation-assistant';
import { getAnalyticsWebClient } from '@atlassian/help-center-common-util/analytics/client';
import { ConversationExperience } from '../../conversation';
import messages from './messages';

interface Props {
    accountId: string;
}

const UnifiedHelpSearchTextBox = (props: Props) => {
    const [isChoiceOpen, setIsChoiceOpen] = useState(false);
    const [isUHExperienceSelected, setIsUHExperienceSelected] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [{ location }, { push }] = useRouter();
    const { formatMessage } = useIntl();
    const [, { startNewConversationWithAiMate, setProviderAuthStatus }] = useConversation();
    const analyticsClient = getAnalyticsWebClient().getInstance();

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        setProviderAuthStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openChoicesOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        setSearchQuery(newQuery);
        newQuery.trim() !== '' ? setIsChoiceOpen(true) : setIsChoiceOpen(false);
    }, []);

    const handleCrossClick = useCallback(() => {
        setSearchQuery('');
        setIsChoiceOpen(false);
    }, []);

    const redirectToAllResults = useCallback(() => {
        if (searchQuery.trim() === '') return;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendUIEvent({
            source: 'hcHomeScreen',
            action: 'clicked',
            actionSubject: 'searchResults',
            actionSubjectId: 'searchTextBox',
            attributes: {
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });
        push(`${location.pathname}?q=${searchQuery.trim()}`) as unknown as void;
    }, [analyticsClient, push, location.pathname, searchQuery]);

    const redirectToUHExperience = useCallback(() => {
        if (searchQuery.trim() === '') return;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendUIEvent({
            source: 'hcHomeScreen',
            action: 'clicked',
            actionSubject: 'UHExperience',
            actionSubjectId: 'searchTextBox',
            attributes: {
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });
        startNewConversationWithAiMate(searchQuery.trim(), { accountId: props.accountId });
        setIsUHExperienceSelected(true);
    }, [analyticsClient, startNewConversationWithAiMate, searchQuery, props.accountId]);

    const handleKeyDown = useCallback((callback: typeof redirectToUHExperience | typeof redirectToAllResults) => {
        // TODO: handle this better see if we can use existing components or ref to achieve this
        return (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const currentActive = document.activeElement;
                currentActive === document.getElementById('conversation-dropdown-box-option-1')
                    ? focus('conversation-dropdown-box-option-2')
                    : focus('conversation-dropdown-box-option-1');
            }
            if (event.key === 'Enter') {
                callback();
            }
        };
    }, []);

    return (
        <>
            <Stack>
                <Box xcss={searchBoxStyles} id={'new-search-textbox'}>
                    <Inline>
                        <SearchIconWrapper>
                            <SearchIcon primaryColor={token('color.icon', 'gray')} label={'search-button'} />
                        </SearchIconWrapper>
                        <Input
                            type="text"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={
                                isHelpCenterAIAnswersEAPEnabled()
                                    ? formatMessage(messages.newInputPlaceholder)
                                    : formatMessage(messages.inputPlaceholder)
                            }
                            onChange={openChoicesOnChange}
                            onKeyDown={handleKeyDown(redirectToUHExperience)}
                            value={searchQuery}
                        />
                        {isChoiceOpen && (
                            <CrossIconButton>
                                <Button
                                    appearance="subtle"
                                    iconBefore={<EditorCloseIcon label={'clear-button'} />}
                                    onClick={handleCrossClick}
                                />
                            </CrossIconButton>
                        )}
                    </Inline>
                </Box>
                {isChoiceOpen && (
                    <DropDownBox id={'conversation-dropdown-box'}>
                        <Stack grow="fill">
                            <DropDownCard
                                aria-label="button"
                                onClick={redirectToUHExperience}
                                role="button"
                                tabIndex={0}
                                onKeyDown={handleKeyDown(redirectToUHExperience)}
                                id={'conversation-dropdown-box-option-1'}
                            >
                                <img src={atlassianIntelligenceIconUrl} alt="" />
                                <QueryText>{searchQuery}</QueryText>
                                <ButtonText>
                                    {isHelpCenterAIAnswersEAPEnabled()
                                        ? formatMessage(messages.askAtlassianIntelligenceForHelp)
                                        : formatMessage(messages.askAtlassianIntelligence)}
                                </ButtonText>
                                <EnterIcon src={enterIconUrl} alt="" />
                            </DropDownCard>
                            <DropDownCard
                                aria-label="button"
                                onClick={redirectToAllResults}
                                role="button"
                                tabIndex={0}
                                onKeyDown={handleKeyDown(redirectToAllResults)}
                                id={'conversation-dropdown-box-option-2'}
                            >
                                <img src={allResultsSearchIcon} alt="" />
                                <QueryText>{searchQuery}</QueryText>
                                <ButtonText>{formatMessage(messages.showAllResults)}</ButtonText>
                            </DropDownCard>
                        </Stack>
                    </DropDownBox>
                )}
            </Stack>
            {isUHExperienceSelected && (
                <SmartCardProvider>
                    <ConversationExperience />
                </SmartCardProvider>
            )}
        </>
    );
};

const selector = createSelector(getAccountId, (accountId) => ({ accountId }));

export default connect(selector, {})(UnifiedHelpSearchTextBox);

const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${mixins.placeholder(token('color.text.subtlest', 'gray'))};
    padding: ${grid.multiple(2.125).px};
    padding-right: ${token('space.400', '32px')};
    padding-left: ${token('space.100', '5px')};
    width: 90%;
    border: 0;
    outline: none;
    position: relative;
    z-index: ${searchTextbox};
`;

const CrossIconButton = styled.div`
    position: absolute;
    padding: 0 ${token('space.200', '16px')};
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    z-index: ${searchTextbox};
`;

const SearchIconWrapper = styled.div`
    padding: 0 ${token('space.200', '16px')};
    height: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    align-self: center;
    padding-right: ${token('space.050', '5px')};
    z-index: ${searchTextbox};
`;

const DropDownCard = styled.div`
    display: flex;
    flex-direction: row;
    height: ${token('space.600', '50px')};
    width: '100%';
    justify-content: space-between;
    align-items: center;
    padding: ${token('space.300', '25px')};
    padding-top: ${token('space.400', '30px')};
    padding-bottom: ${token('space.400', '30px')};
    cursor: pointer;
    overflow: hidden;

    &:hover {
        background-color: ${token('color.background.accent.gray.subtler', '#0000000f')};
        border-radius: ${token('space.050', '4px')};
    }

    &:focus {
        outline-color: ${token('color.border.accent.blue', '#6495ed')};
    }
`;

const QueryText = styled.div`
    flex-grow: 1;
    margin-left: ${token('space.150', '10px')};
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
`;

const ButtonText = styled.p`
    color: ${token('color.text.subtlest', '#626F86')};
    margin: 0;
    text-wrap: nowrap;
`;

const EnterIcon = styled.img`
    margin-left: ${token('space.150', '10px')};
`;

const searchBoxStyles = xcss({
    position: 'relative',
    background: token('color.background.input', colors.N0),
    borderRadius: 'border.radius.100',
});

const DropDownBox = styled.div`
    display: flex;
    border-radius: ${token('border.radius.100', '4px')};
    background: ${token('color.background.input', colors.N0)};
    margin-top: ${token('space.800', '60px')};
    width: 100%;
    max-width: ${token('space.800', '744px')};
    position: absolute;
    box-shadow: ${token('elevation.shadow.raised', `0 5px 5px #091e4229`)};

    ${bp.fromCustom(0, sizes.small).css`
        width: calc(100% - 64px);
    `};
`;
